/**
 * Bug Report Service
 *
 * Handles bug report submission via API Gateway to Lambda function.
 * The Lambda handles DynamoDB storage, S3 uploads, and SNS notifications.
 */

class BugReportService {
  constructor() {
    // API Gateway endpoint from environment variables
    this.API_URL = process.env.REACT_APP_BUG_TRACKER_API_URL || '';
  }

  /**
   * Check if service is configured
   */
  isConfigured() {
    return !!this.API_URL;
  }

  /**
   * Submit a bug report via API Gateway
   * @param {Object} reportData - Bug report data
   * @returns {Promise<Object>} - API response with reportId
   */
  async submitBugReport(reportData, accessToken = null) {
    try {
      if (!this.isConfigured()) {
        throw new Error('Bug tracker service not properly configured. API URL is missing.');
      }

      // Convert files to base64 for API transmission
      const screenshots = await this.filesToBase64(reportData.screenshots || []);
      const templates = await this.filesToBase64(reportData.templates || []);

      // Prepare request payload
      const payload = {
        title: reportData.title || 'Untitled Issue',
        description: reportData.description || '',
        userName: reportData.userName || 'Anonymous',
        userEmail: reportData.userEmail || '',
        platform: reportData.platform || 'unknown',
        issueType: reportData.issueType || 'bug',
        severity: reportData.severity || 'medium',
        userAgent: reportData.userAgent || navigator.userAgent,
        currentUrl: reportData.currentUrl || window.location.href,
        screenshots: screenshots,
        templates: templates
      };

      // Call API Gateway
      const headers = {
        'Content-Type': 'application/json'
      };
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(this.API_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API request failed with status ${response.status}`);
      }

      const result = await response.json();

      return {
        success: true,
        reportId: result.reportId,
        message: result.message || 'Bug report submitted successfully'
      };

    } catch (error) {
      console.error('Error submitting bug report:', error);
      throw error;
    }
  }

  /**
   * Convert File objects to base64 for API transmission
   * @param {Array} files - Array of File objects
   * @returns {Promise<Array>} - Array of file objects with base64 data
   */
  async filesToBase64(files) {
    const converted = [];

    for (const file of files) {
      try {
        const base64 = await this.fileToBase64(file);
        converted.push({
          name: file.name,
          type: file.type,
          size: file.size,
          data: base64
        });
      } catch (error) {
        console.error(`Error converting file ${file.name} to base64:`, error);
        // Continue with other files even if one fails
      }
    }

    return converted;
  }

  /**
   * Convert a single File to base64
   * @param {File} file - File object
   * @returns {Promise<string>} - Base64 encoded file data
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove the data:*/*;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Get severity display properties
   * @param {string} severity - Severity level
   * @returns {Object} - Severity display properties
   */
  getSeverityProps(severity) {
    const severityMap = {
      low: { color: 'blue', label: 'Low' },
      medium: { color: 'yellow', label: 'Medium' },
      high: { color: 'orange', label: 'High' },
      critical: { color: 'red', label: 'Critical' }
    };
    return severityMap[severity] || severityMap.medium;
  }

  /**
   * Get issue type display properties
   * @param {string} issueType - Issue type
   * @returns {Object} - Issue type display properties
   */
  getIssueTypeProps(issueType) {
    const typeMap = {
      bug: { color: 'red', label: 'Bug', icon: '🐛' },
      'feature-request': { color: 'blue', label: 'Feature Request', icon: '💡' },
      question: { color: 'green', label: 'Question', icon: '❓' },
      other: { color: 'gray', label: 'Other', icon: '📝' }
    };
    return typeMap[issueType] || typeMap.bug;
  }
}

// Export singleton instance
export const bugReportService = new BugReportService();
export default bugReportService;
